import { Controller } from "@hotwired/stimulus";
import exifr from "exifr";
import { format } from "date-fns";

export default class extends Controller {
  static targets = [
    "numberOfPhotos",
    "fileInput",
    "formContainer",
    "albumPhotoFieldset",
    "formTemplate",
    "unsavedNotice",
  ];
  static values = { maxAlbumPhotos: Number, nextIndex: Number };

  connect() {
    this.#refreshList();
  }

  addPhotos() {
    if (this.#alertIfFull()) {
      return;
    }

    this.fileInputTarget.click();
  }

  async selectPhotos(event) {
    // 選択順の降順（最後に選んだものが上）にし、保存後の id 降順表示と揃える
    const files = Array.from(event.target.files).reverse();
    const fragment = document.createDocumentFragment();

    for (const file of files) {
      if (
        this.#alertIfFull(this.#numberOfPhotos() + fragment.childElementCount)
      ) {
        break;
      }

      fragment.appendChild(
        await this.#buildFieldset(file, this.nextIndexValue)
      );
      this.nextIndexValue++;
    }

    this.formContainerTarget.prepend(fragment);
    this.#refreshList();

    event.target.value = "";
  }

  unselectPhoto(event) {
    const fieldset = this.#fieldsetFrom(event);
    this.#revokePreview(fieldset);
    fieldset.hidden = true;

    if (event.params.rendering === "csr") {
      fieldset.disabled = true;
    } else {
      this.#destroyField(fieldset).value = "1";
    }

    this.#updateNumberOfPhotos();
    this.#updatePostNumbers();
    this.#refreshUnsaved(fieldset);
  }

  async changePhoto(event) {
    const file = event.target.files[0];

    if (file === void 0) {
      return;
    }

    const fieldset = this.#fieldsetFrom(event);
    await this.#assignPhoto(fieldset, file);
    fieldset.dataset.photoDirty = "true";
    this.#refreshUnsaved(fieldset);
  }

  syncFieldsetUnsavedState(event) {
    if (event.target.type === "file") {
      return;
    }

    const fieldset = this.#fieldsetFrom(event);
    if (!fieldset || fieldset.hidden) {
      return;
    }

    this.#refreshUnsaved(fieldset);
  }

  #visibleFieldsets() {
    return this.albumPhotoFieldsetTargets.filter(
      (fieldset) => fieldset.hidden === false
    );
  }

  #numberOfPhotos() {
    return this.#visibleFieldsets().length;
  }

  #alertIfFull(count = this.#numberOfPhotos()) {
    if (count >= this.maxAlbumPhotosValue) {
      alert(`写真は${this.maxAlbumPhotosValue}枚までしか追加できません`);
      return true;
    }

    return false;
  }

  #updateNumberOfPhotos() {
    this.numberOfPhotosTarget.textContent = this.#numberOfPhotos();
  }

  #updatePostNumbers() {
    const fieldsets = this.#visibleFieldsets();

    fieldsets.forEach((fieldset, i) => {
      const numberEl = this.#postNumber(fieldset);
      if (numberEl) {
        numberEl.textContent = fieldsets.length - i;
      }
    });
  }

  #hasUnsavedChanges() {
    return this.albumPhotoFieldsetTargets.some((fieldset) =>
      this.#isUnsaved(fieldset)
    );
  }

  #updateUnsavedNotice() {
    this.unsavedNoticeTarget.hidden = !this.#hasUnsavedChanges();
  }

  async #buildFieldset(file, index) {
    const template = document.createElement("template");
    template.innerHTML = this.formTemplateTarget.innerHTML.replaceAll(
      "NEW_RECORD",
      String(index)
    );
    const fieldset = template.content.firstElementChild;

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    fieldset.querySelector('input[type="file"]').files = dataTransfer.files;
    await this.#assignPhoto(fieldset, file);

    return fieldset;
  }

  async #assignPhoto(fieldset, file) {
    this.#revokePreview(fieldset);
    this.#image(fieldset).src = URL.createObjectURL(file);

    const takenAt = await this.#extractTakenAt(file);
    this.#takenAtHiddenField(fieldset).value = takenAt.toJSON();
    this.#photoTakenAt(fieldset).textContent =
      this.#formatPhotoTakenAt(takenAt);
  }

  #refreshList() {
    this.#updateNumberOfPhotos();
    this.#updatePostNumbers();
    this.#updateUnsavedNotice();
  }

  async #extractTakenAt(file) {
    const output = await exifr.parse(file, ["DateTimeOriginal"]);
    return output?.DateTimeOriginal === void 0
      ? new Date()
      : output.DateTimeOriginal;
  }

  #formatPhotoTakenAt(takenAt) {
    return format(takenAt, "M/d H:mm");
  }

  #isUnsaved(fieldset) {
    if (fieldset.hidden) {
      return this.#destroyField(fieldset)?.value === "1";
    }

    const comment = this.#commentInput(fieldset);
    const commentChanged =
      (comment?.value ?? "") !== (comment?.defaultValue ?? "");

    return (
      fieldset.dataset.baselineUnsaved === "true" ||
      fieldset.dataset.photoDirty === "true" ||
      commentChanged
    );
  }

  #refreshUnsaved(fieldset) {
    fieldset.classList.toggle("is-unsaved", this.#isUnsaved(fieldset));
    this.#updateUnsavedNotice();
  }

  #fieldsetFrom(event) {
    return event.target.closest(
      '[data-album-photos-form-target="albumPhotoFieldset"]'
    );
  }

  #postNumber(fieldset) {
    return fieldset.querySelector(
      '[data-album-photos-form-target="postNumber"]'
    );
  }

  #image(fieldset) {
    return fieldset.querySelector('[data-album-photos-form-target="image"]');
  }

  #takenAtHiddenField(fieldset) {
    return fieldset.querySelector(
      '[data-album-photos-form-target="takenAtHiddenField"]'
    );
  }

  #photoTakenAt(fieldset) {
    return fieldset.querySelector(
      '[data-album-photos-form-target="photoTakenAt"]'
    );
  }

  #destroyField(fieldset) {
    return fieldset.querySelector(
      '[data-album-photos-form-target="destroyHiddenField"]'
    );
  }

  #commentInput(fieldset) {
    return fieldset.querySelector('[data-album-photos-form-target="comment"]');
  }

  #revokePreview(fieldset) {
    const image = this.#image(fieldset);
    if (image?.src.startsWith("blob:")) {
      URL.revokeObjectURL(image.src);
    }
  }
}
